import { ChangeDetectionStrategy, Component, computed, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { assetSubResourceBreadcrumb } from '../../../../../../../utils/breadcrumb.utils';
import { MenuItem } from 'primeng/api';
import { firstValueFrom } from 'rxjs';
import {
  FORBIDDEN_AUTOMATIC_EXTRA_TYPES,
  AUTOMATIC_ALLOWED_PARAMETER_TYPES,
  automaticMeasureDefinition,
} from '../../../../../../../entity-definitions/automatic-measure.definition';
import { AutomaticMeasuresService } from '../../../../../../../services/api/generated/automatic-measures/automatic-measures.service';
import type { AutomaticMeasureCreatePayload } from '../../../../../../../services/api/generated/model';
import { AutomaticFrequency } from '../../../../../../../types/measure.interface';
import { AnalysisPostStore } from '../../../../../../../stores/analysis-post.store';
import { AssetStore } from '../../../../../../../stores/asset.store';
import { AuscultationInformationStore } from '../../../../../../../stores/auscultation-info.store';
import { LocationStore } from '../../../../../../../stores/location.store';
import { ManualMeasureStore } from '../../../../../../../stores/manual-measure.store';
import { MeasureTypeStore } from '../../../../../../../stores/measure-type.store';
import { AutomaticMeasureStore } from '../../../../../../../stores/automatic-measure.store';
import { UnitStore } from '../../../../../../../stores/unit.store';
import { CesAssetBreadcrumbComponent } from '../../../../../../ui/ces-asset-breadcrumb/ces-asset-breadcrumb.component';
import { CesWizardStepperComponent } from '../../../../../../ui/ces-wizard-stepper/ces-wizard-stepper.component';
import { ManualMeasureFormulaComponent } from '../../manual/new/manual-measure-formula.component';
import { ManualMeasureVerificationComponent } from '../../manual/new/manual-measure-verification.component';
import { BaseMeasureWizardComponent } from '../../shared/base-measure-wizard.component';
import { filterMeasuresByAsset } from '../../shared/measure-asset-filter';
import { AutomaticMeasureInfoComponent } from './automatic-measure-info.component';
import { FormulaParameter, ParameterTypes } from '../../../../../../../types/formula.interface';
import { CheckerType } from '../../../../../../../types/measure-checker.interface';
import { formulaCompletionValidator, verificationCompletionValidator } from '../../shared/measure-step-validators';
import { ToastService } from '../../../../../../../services/toast.service';
import { isConflictWithCode } from '../../../../../../../utils/http-error.utils';
import { MEASURE_FORMULA_REQUIRED, MEASURE_NAME_ALREADY_EXISTS } from '../../../../../../../utils/measure-error-codes';
import { startOfDay } from '../../../../../../../utils/date-range-filter.utils';
import { UniqueNameEntry } from '../../shared/base-measure-info.component';
import { extractServerErrorCode } from '../../formula-period';

export { FORBIDDEN_AUTOMATIC_EXTRA_TYPES, AUTOMATIC_ALLOWED_PARAMETER_TYPES };

export const buildAcquisitionParameter = (): FormulaParameter =>
  ({
    type: ParameterTypes.ACQUISITION,
    symbol: '',
    name: '',
    sortNr: 1,
  }) as unknown as FormulaParameter;

const toIsoString = (value: Date | string | null | undefined): string | null => {
  if (value == null) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value.toISOString();
  return typeof value === 'string' ? value : null;
};

export interface AutomaticMeasureWizardFormValue {
  info: {
    name: string;
    externalId: string;
    measureTypeId: number | null;
    frequency: AutomaticFrequency | null;
    hour: string;
    unitId: number | null;
    locationIds: number[];
    description: string;
    dayOfWeek: number | null;
    dayOfMonth: number | null;
    month: number | null;
    overwrite: boolean;
  };
  formula: {
    startTs: Date | string | null;
    parameters: FormulaParameter[];
    expression: string;
  };
  verification: {
    startTs: Date | string | null;
    type: CheckerType;
    negativeGap: number | null;
    positiveGap: number | null;
    firstParameters: FormulaParameter[];
    firstExpression: string;
    secondParameters: FormulaParameter[];
    secondExpression: string;
  };
  device: {
    deviceId: number | null;
    deviceSettings: string;
  };
}

export const toApiFormulaParameter = (p: FormulaParameter): Record<string, unknown> => {
  const source = p as FormulaParameter & {
    name?: string;
    value?: number | string | null;
    defaultValue?: number | string | null;
    unitId?: number | null;
    measureId?: number | null;
    sortNr?: number | null;
  };
  const out: Record<string, unknown> = {
    type: source.type,
    symbol: source.symbol,
  };
  if (typeof source.name === 'string' && source.name.length > 0) out['name'] = source.name;
  if (source.sortNr != null) out['sortNr'] = source.sortNr;
  if (source.unitId != null) out['unitId'] = source.unitId;
  if (source.type === ParameterTypes.ACQUISITION) {
    // SCADA injects the runtime value; only the user-maintained unit travels with it.
    return out;
  }
  if (source.value != null && source.value !== '') out['value'] = Number(source.value);
  if (source.defaultValue != null && source.defaultValue !== '') out['defaultValue'] = Number(source.defaultValue);
  if (source.measureId != null) out['measureId'] = source.measureId;
  return out;
};

@Component({
  selector: 'app-automatic-measure-wizard',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    CesAssetBreadcrumbComponent,
    AutomaticMeasureInfoComponent,
    ManualMeasureFormulaComponent,
    ManualMeasureVerificationComponent,
    CesWizardStepperComponent,
    TranslocoModule,
  ],
  templateUrl: './automatic-measure-wizard.component.html',
})
export class AutomaticMeasureWizardComponent extends BaseMeasureWizardComponent implements OnInit {
  protected readonly automaticMeasureStore = inject(AutomaticMeasureStore);
  protected readonly manualMeasureStore = inject(ManualMeasureStore);
  protected readonly analysisPostStore = inject(AnalysisPostStore);
  protected readonly auscultationInfoStore = inject(AuscultationInformationStore);
  protected readonly measureTypeStore = inject(MeasureTypeStore);
  protected readonly unitStore = inject(UnitStore);
  protected readonly locationsStore = inject(LocationStore);
  protected readonly assetStore = inject(AssetStore);
  private readonly wizardApi = inject(AutomaticMeasuresService);
  private readonly toastService = inject(ToastService);
  private readonly translateService = inject(TranslocoService);

  protected readonly forbiddenExtraParameterTypes = FORBIDDEN_AUTOMATIC_EXTRA_TYPES;
  protected readonly allowedParameterTypes = AUTOMATIC_ALLOWED_PARAMETER_TYPES;

  protected readonly stepKeys = ['info', 'formula', 'verification'] as const;

  readonly breadcrumbTrail = computed<MenuItem[]>(() => {
    this.translations();
    return assetSubResourceBreadcrumb(this.translateService, {
      parentLabelKey: 'navigation.measures',
      parentRouterLink: ['/assets', this.currentAssetId, 'configuration', 'measures', 'automatic'],
      currentLabel: this.translateService.translate(automaticMeasureDefinition.messages.dialogTitles.create()),
    });
  });

  readonly measuresForAsset = computed(() => filterMeasuresByAsset(this.automaticMeasureStore.entities(), this.currentAssetId));

  readonly existingNamesOnAsset = computed<readonly UniqueNameEntry[]>(() => {
    const assetId = this.currentAssetId;
    if (!assetId) return [];
    const sources = [
      ...this.manualMeasureStore.entities(),
      ...this.automaticMeasureStore.entities(),
      ...this.analysisPostStore.entities(),
      ...this.auscultationInfoStore.entities(),
    ];
    const seen = new Set<string>();
    const result: UniqueNameEntry[] = [];
    for (const entity of sources) {
      const record = entity as { assetId?: number; name?: string | null };
      if (record.assetId !== assetId) continue;
      const name = record.name;
      if (!name) continue;
      const key = name.trim().toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      result.push({ name });
    }
    return result;
  });

  protected get infoGroup(): FormGroup {
    return this.wizardForm.get('info') as FormGroup;
  }

  protected get formulaGroup(): FormGroup {
    return this.wizardForm.get('formula') as FormGroup;
  }

  protected get verificationGroup(): FormGroup {
    return this.wizardForm.get('verification') as FormGroup;
  }

  // Pré-remplit les débuts de validité (formule + vérification) à minuit local plutôt qu'à l'heure courante.
  private defaultStartTs(): Date {
    return startOfDay(new Date());
  }

  override ngOnInit(): void {
    this.wizardForm = this.fb.group({
      info: this.fb.group({
        name: ['', [Validators.required, Validators.minLength(3), Validators.maxLength(50)]],
        externalId: ['', [Validators.maxLength(50)]],
        measureTypeId: [null, Validators.required],
        frequency: [AutomaticFrequency.TenMinutes, Validators.required],
        hour: ['00:00', Validators.required],
        unitId: [null, Validators.required],
        locationIds: [[] as number[]],
        description: [''],
        dayOfWeek: [null as number | null],
        dayOfMonth: [null as number | null],
        month: [null as number | null],
        overwrite: [false],
      }),
      formula: this.fb.group(
        {
          startTs: [this.defaultStartTs() as Date | null, Validators.required],
          parameters: [[buildAcquisitionParameter()] as FormulaParameter[]],
          expression: [''],
        },
        { validators: [formulaCompletionValidator] },
      ),
      verification: this.fb.group(
        {
          startTs: [this.defaultStartTs() as Date | null, Validators.required],
          type: [CheckerType.TWO_FIXED_LIMITS, Validators.required],
          negativeGap: [-10 as number | null],
          positiveGap: [10],
          firstParameters: [[] as FormulaParameter[]],
          firstExpression: [''],
          secondParameters: [[] as FormulaParameter[]],
          secondExpression: [''],
        },
        { validators: [verificationCompletionValidator] },
      ),
      device: this.fb.group({
        deviceId: [null as number | null],
        deviceSettings: [''],
      }),
    });

    super.ngOnInit();

    const freqCtrl = this.infoGroup.get('frequency');
    if (freqCtrl) {
      this.applyAnchorsValidators(freqCtrl.value as AutomaticFrequency | null);
      freqCtrl.valueChanges
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(freq => this.applyAnchorsValidators(freq as AutomaticFrequency | null));
    }
  }

  protected applyAnchorsValidators(freq: AutomaticFrequency | null): void {
    const info = this.wizardForm.get('info');
    if (!info) return;
    const anchors = ['dayOfWeek', 'dayOfMonth', 'month']
      .map(name => info.get(name))
      .filter((c): c is NonNullable<typeof c> => c !== null);
    for (const c of anchors) {
      c.clearValidators();
      c.setValue(null, { emitEvent: false });
    }
    const dow = info.get('dayOfWeek');
    const dom = info.get('dayOfMonth');
    const m = info.get('month');
    if (freq === AutomaticFrequency.OneWeek) {
      dow?.setValidators([Validators.required, Validators.min(1), Validators.max(7)]);
    } else if (freq === AutomaticFrequency.OneMonth) {
      dom?.setValidators([Validators.required, Validators.min(1), Validators.max(31)]);
    } else if (
      freq != null &&
      [AutomaticFrequency.FourMonths, AutomaticFrequency.SixMonths, AutomaticFrequency.OneYear].includes(freq)
    ) {
      dom?.setValidators([Validators.required, Validators.min(1), Validators.max(31)]);
      m?.setValidators([Validators.required, Validators.min(1), Validators.max(12)]);
    }
    for (const c of anchors) {
      c.updateValueAndValidity({ emitEvent: false });
    }
  }

  protected async loadInitialData(): Promise<void> {
    try {
      if (this.currentAssetId) {
        await Promise.all([
          this.assetStore.loadById(this.currentAssetId),
          this.automaticMeasureStore.getByAssetId(this.currentAssetId),
          this.manualMeasureStore.getByAssetId(this.currentAssetId),
          this.analysisPostStore.getByAssetId(this.currentAssetId),
          this.auscultationInfoStore.getByAssetId(this.currentAssetId),
          this.measureTypeStore.loadAll(),
          this.unitStore.loadAll(),
          this.locationsStore.getLocationsByAsset(this.currentAssetId),
        ]);
      }
    } catch (error) {
      console.error(this.translateService.translate('common.errors.dataLoadFailed'), error);
    }
  }

  protected async submitForm(): Promise<void> {
    if (this.wizardForm.invalid || this.submitting) {
      this.wizardForm.markAllAsTouched();
      this.toastService.error(this.translateService.translate('common.errors.formInvalid'), true);
      return;
    }

    this.submitting = true;
    const formData = this.wizardForm.getRawValue() as AutomaticMeasureWizardFormValue;

    if (this.currentAssetId === null) {
      this.submitting = false;
      return;
    }
    const assetId = this.currentAssetId;

    try {
      const payload = this.buildPayload(formData, assetId);
      const result = await firstValueFrom(
        this.wizardApi.postAutomaticMeasureList(payload as unknown as AutomaticMeasureCreatePayload),
      );

      if (result?.id) {
        void this.router.navigate(['../../../measures/automatic', result.id], { relativeTo: this.route });
      } else {
        void this.router.navigate(['../../..'], { relativeTo: this.route });
      }
    } catch (error) {
      const messageKey = this.resolveSubmitErrorKey(error);
      this.toastService.error(messageKey, undefined, undefined, undefined, { name: formData.info.name });
      console.error(this.translateService.translate('common.errors.saveFailed'), error);
    } finally {
      this.submitting = false;
    }
  }

  private resolveSubmitErrorKey(error: unknown): string {
    if (extractServerErrorCode(error) === MEASURE_FORMULA_REQUIRED) {
      return 'entities.measureFormula.messages.formulaRequired';
    }
    return isConflictWithCode(error, MEASURE_NAME_ALREADY_EXISTS)
      ? 'entities.automaticMeasure.messages.duplicateError'
      : 'common.errors.saveFailed';
  }

  getTitle(): string {
    const name = this.wizardForm.get('info.name')?.value as string | null;
    const base = this.translateService.translate(automaticMeasureDefinition.messages.dialogTitles.create());
    const trimmed = name?.trim();
    return trimmed && trimmed.length >= 3
      ? this.translateService.translate('common.wizard.titleWithName', { base, name: trimmed.substring(0, 50) })
      : base;
  }

  private buildPayload(formData: AutomaticMeasureWizardFormValue, assetId: number): Record<string, unknown> {
    const payload: Record<string, unknown> = {
      name: formData.info.name,
      externalId: formData.info.externalId,
      measureTypeId: formData.info.measureTypeId,
      frequency: formData.info.frequency,
      hour: formData.info.hour,
      dayOfWeek: formData.info.dayOfWeek,
      dayOfMonth: formData.info.dayOfMonth,
      month: formData.info.month,
      unitId: formData.info.unitId,
      assetId,
      description: formData.info.description,
      locationIds: formData.info.locationIds,
      overwrite: formData.info.overwrite,

      formulaStartTs: toIsoString(formData.formula.startTs),
      formulaParameters: formData.formula.parameters.map(toApiFormulaParameter),
      formulaExpression: formData.formula.expression,

      deviceId: formData.device.deviceId,
      deviceSettings: formData.device.deviceSettings,
      priority: false,
    };

    if (formData.verification.type === CheckerType.NONE) return payload;

    payload['verificationType'] = formData.verification.type;
    payload['verificationStartTs'] = toIsoString(formData.verification.startTs);

    if (
      formData.verification.type === CheckerType.TWO_FIXED_LIMITS ||
      formData.verification.type === CheckerType.POLYNOMIAL_AND_RANGES
    ) {
      payload['negativeGap'] = formData.verification.negativeGap;
      payload['positiveGap'] = formData.verification.positiveGap;
    }
    if (
      formData.verification.type === CheckerType.TWO_POLYNOMIALS ||
      formData.verification.type === CheckerType.POLYNOMIAL_AND_RANGES
    ) {
      payload['formulaFirstParameters'] = (formData.verification.firstParameters ?? []).map(toApiFormulaParameter);
      payload['formulaFirstExpression'] = formData.verification.firstExpression;
    }
    if (formData.verification.type === CheckerType.TWO_POLYNOMIALS) {
      payload['formulaSecondParameters'] = (formData.verification.secondParameters ?? []).map(toApiFormulaParameter);
      payload['formulaSecondExpression'] = formData.verification.secondExpression;
    }

    return payload;
  }
}
